import { state } from '../lib/state.js';
import { supabase } from '../lib/supabase.js';
import { loadLineCalculatorSettings, saveLineCalculatorSettings } from '../services/repository.js';
import { startBrowserlessCalculatorJob } from '../services/browserless-calculator-bridge.js';
import { els } from '../app/dom.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { setStatus, localDate, normalizeClock, sectionDistanceKm, formatDuration } from '../lib/app-helpers.js';
import { prepareSectionMetrics } from './common.js';

const MYTO_SETTINGS_KEY = 'd24-myto-cz-settings';
const MYTO_BORDER_OPTIONS_CACHE_KEY = 'd24-myto-cz-border-options-v21';
const MYTO_BORDER_OPTIONS_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MYTO_CZ_MAX_POINTS = 5;
let pendingMytoSection = null;
let pendingMytoSavedSettings = null;
let mytoBorderOptionsPromise = null;

export function isMytoCzSection(section) {
  return Boolean(section) && (
    section.calculatorKey === 'myto-cz'
    || section.countryCode === 'CZ'
  );
}

export function mytoCzPointLimitExceeded(section) {
  return isMytoCzSection(section) && Array.isArray(section?.points) && section.points.length > MYTO_CZ_MAX_POINTS;
}

export function mytoCzLimitMessage(section) {
  const count = Array.isArray(section?.points) ? section.points.length : 0;
  const remove = Math.max(0, count - MYTO_CZ_MAX_POINTS);
  return `MYTO CZ obsługuje maksymalnie ${MYTO_CZ_MAX_POINTS} punktów na odcinku Czechy (początek + maks. 3 punkty „Przez” + cel). Ten odcinek ma ${count} punktów.${remove ? ` Usuń co najmniej ${remove} ${remove === 1 ? 'punkt' : 'punkty'} i ponownie wyznacz trasę.` : ''}`;
}

export function alertMytoCzPointLimit(section) {
  const message = mytoCzLimitMessage(section);
  window.alert(`Uwaga — limit MYTO CZ\n\n${message}`);
  return message;
}

function setMytoBorderStatus(message, kind='neutral'){
  if(!els.mytoBordersStatus)return;
  els.mytoBordersStatus.textContent=message;
  els.mytoBordersStatus.className=`myto-border-status ${kind}`;
}

function parseMytoBorderCache(){
  try{
    const parsed=JSON.parse(localStorage.getItem(MYTO_BORDER_OPTIONS_CACHE_KEY)||'null');
    if(!parsed||!Array.isArray(parsed.entryOptions)||!Array.isArray(parsed.exitOptions))return null;
    const age=Date.now()-Number(parsed.fetchedAt||0);
    if(!Number.isFinite(age)||age<0||age>MYTO_BORDER_OPTIONS_CACHE_TTL_MS)return null;
    return parsed;
  }catch{
    return null;
  }
}

function normalizeMytoBorderOptionList(items){
  const seen=new Set();
  const output=[];
  for(const item of Array.isArray(items)?items:[]){
    const value=String(item?.value||'').trim();
    const label=String(item?.label||item?.text||'').replace(/\s+/g,' ').trim();
    if(!value||!label||seen.has(value))continue;
    seen.add(value);
    output.push({value,label});
  }
  return output;
}

async function fetchMytoBorderOptionsFromEdge(){
  if(!await requireAuthenticatedSession('pobrać listę przejść granicznych MYTO CZ')){
    throw new Error('Sesja użytkownika wygasła.');
  }

  const requestId=crypto.randomUUID();
  const {data,error}=await supabase.functions.invoke('myto-cz-browserless',{
    body:{
      action:'list-border-options',
      calculatorKey:'myto-cz',
      jobId:requestId,
      requestId,
    },
  });

  if(error){
    let message=error.message||'Edge Function MYTO CZ nie zwróciła listy przejść.';
    try{
      if(error.context&&typeof error.context.json==='function'){
        const body=await error.context.json();
        if(body?.error)message=body.error;
      }
    }catch{}
    throw new Error(message);
  }
  if(!data?.ok||!data?.result){
    throw new Error(data?.error||'MYTO CZ nie zwróciło listy przejść granicznych.');
  }

  const entryOptions=normalizeMytoBorderOptionList(data.result.entryOptions);
  const exitOptions=normalizeMytoBorderOptionList(data.result.exitOptions);
  if(!entryOptions.length||!exitOptions.length){
    throw new Error('MYTO CZ zwróciło pustą listę przejść granicznych.');
  }

  const cache={
    fetchedAt:Date.now(),
    sourceFetchedAt:data.result.fetchedAt||new Date().toISOString(),
    runnerVersion:data.result.runnerVersion||'',
    entryOptions,
    exitOptions,
  };
  localStorage.setItem(MYTO_BORDER_OPTIONS_CACHE_KEY,JSON.stringify(cache));
  return cache;
}

export async function loadMytoBorderOptions({force=false,showStatus=false}={}){
  if(!force){
    const cached=parseMytoBorderCache();
    if(cached){
      if(showStatus)setMytoBorderStatus(`Lista MYTO CZ: ${cached.entryOptions.length} wjazdowych / ${cached.exitOptions.length} wyjazdowych.`, 'ok');
      return cached;
    }
  }

  if(mytoBorderOptionsPromise)return mytoBorderOptionsPromise;

  if(showStatus)setMytoBorderStatus('Pobieram pełną listę przejść bezpośrednio z MYTO CZ…','working');
  if(els.refreshMytoBordersBtn)els.refreshMytoBordersBtn.disabled=true;

  mytoBorderOptionsPromise=fetchMytoBorderOptionsFromEdge()
    .then(cache=>{
      if(showStatus)setMytoBorderStatus(`Pobrano z MYTO CZ: ${cache.entryOptions.length} przejść wjazdowych / ${cache.exitOptions.length} wyjazdowych. Lista jest cache’owana 30 dni.`, 'ok');
      return cache;
    })
    .finally(()=>{
      mytoBorderOptionsPromise=null;
      if(els.refreshMytoBordersBtn)els.refreshMytoBordersBtn.disabled=false;
    });

  return mytoBorderOptionsPromise;
}

function selectedMytoOption(select){
  if(!select)return {value:'',label:''};
  const value=String(select.value||'').trim();
  const option=Array.from(select.options||[]).find(item=>String(item.value||'')===value);
  return {
    value,
    label:String(option?.textContent||'').replace(/\s+/g,' ').trim(),
  };
}

function fillMytoBorderSelect(select, options, savedValue='', savedLabel=''){
  if(!select)return;
  const normalized=normalizeMytoBorderOptionList(options);
  select.innerHTML='<option value="">— wybierz przejście z listy MYTO CZ —</option>';

  for(const item of normalized){
    const option=document.createElement('option');
    option.value=item.value;
    option.textContent=item.label;
    select.appendChild(option);
  }

  const wanted=String(savedValue||'').trim();
  if(wanted&&normalized.some(item=>item.value===wanted)){
    select.value=wanted;
  }else if(wanted){
    const legacy=document.createElement('option');
    legacy.value=wanted;
    legacy.textContent=`${savedLabel||wanted} — zapisane wcześniej; sprawdź`;
    legacy.dataset.legacy='true';
    select.appendChild(legacy);
    select.value=wanted;
  }else{
    select.value='';
  }
}

function configureMytoBorderRequirement(field, select, required){
  field?.classList.toggle('is-not-required',!required);
  if(select){
    select.required=Boolean(required);
    select.disabled=!required;
    if(!required)select.value='';
  }
}

export function populateMytoBorderSelects(cache, section, savedSettings={}){
  const points=Array.isArray(section?.points)?section.points:[];
  const entryRequired=isMytoBorderPoint(points[0]);
  const exitRequired=isMytoBorderPoint(points.at(-1));

  configureMytoBorderRequirement(els.mytoEntryBorderField,els.mytoEntryBorder,entryRequired);
  configureMytoBorderRequirement(els.mytoExitBorderField,els.mytoExitBorder,exitRequired);

  const sectionKey=String(Number(section?.order||0));
  const sectionSaved=savedSettings?.sectionRoutes&&typeof savedSettings.sectionRoutes==='object'
    ? (savedSettings.sectionRoutes[sectionKey]||{})
    : {};
  const merged={...savedSettings,...sectionSaved};

  fillMytoBorderSelect(
    els.mytoEntryBorder,
    cache?.entryOptions||[],
    merged.entryBorderValue||'',
    merged.entryBorderLabel||''
  );
  fillMytoBorderSelect(
    els.mytoExitBorder,
    cache?.exitOptions||[],
    merged.exitBorderValue||'',
    merged.exitBorderLabel||''
  );

  if(!entryRequired&&!exitRequired){
    setMytoBorderStatus('Ten czeski odcinek zaczyna się i kończy w miejscowości — wybór przejść granicznych nie jest potrzebny.','ok');
  }
}

export async function openMytoDialog(section){
  if(mytoCzPointLimitExceeded(section)){
    const message=alertMytoCzPointLimit(section);
    setStatus(els.extensionStatus,message,'warning');
    return;
  }

  pendingMytoSection=section;
  const localDefaults=loadMytoSettings();
  let savedForLine=null;
  try{
    savedForLine=await loadLineCalculatorSettings(
      state.organizationId,
      state.currentRoute.lineNumber,
      'myto-cz'
    );
  }catch(error){
    console.warn('[D24 MYTO CZ] Nie udało się wczytać parametrów linii:',error);
  }
  pendingMytoSavedSettings=savedForLine||{};

  const sectionKey=String(Number(section?.order||0));
  const sectionSaved=savedForLine?.sectionRoutes&&typeof savedForLine.sectionRoutes==='object'
    ? (savedForLine.sectionRoutes[sectionKey]||{})
    : {};
  const defaults={
    vehicleCategory:'6',
    emissionClass:'6',
    co2Class:'1',
    ...localDefaults,
    ...(savedForLine||{}),
    ...sectionSaved,
  };
  const now=new Date();

  els.mytoDate.value=localDate(now);
  els.mytoTime.value=now.toTimeString().slice(0,8);
  els.mytoVehicleCategory.value=defaults.vehicleCategory||'6';
  els.mytoBaseAxles.value=defaults.baseAxles||'';
  els.mytoActualAxles.value=defaults.actualAxles||defaults.baseAxles||'';
  els.mytoEmissionClass.value=defaults.emissionClass||'6';
  els.mytoCo2Class.value=defaults.co2Class||'1';
  els.mytoDialogRoute.textContent=`${section.countryName}: ${section.points.map(point=>point.name).join(' → ')}`;

  // Otwieramy modal od razu. Lista granic dociąga się w tle, a zapisane
  // przejścia są zaznaczane po otrzymaniu pełnej listy z MYTO.
  els.mytoDialog.classList.remove('hidden');
  els.mytoDialog.setAttribute('aria-hidden','false');
  document.body.style.overflow='hidden';

  const points=Array.isArray(section?.points)?section.points:[];
  configureMytoBorderRequirement(els.mytoEntryBorderField,els.mytoEntryBorder,isMytoBorderPoint(points[0]));
  configureMytoBorderRequirement(els.mytoExitBorderField,els.mytoExitBorder,isMytoBorderPoint(points.at(-1)));
  setMytoBorderStatus('Sprawdzam pełną listę przejść MYTO CZ…','working');

  try{
    const cache=await loadMytoBorderOptions({showStatus:true});
    // Użytkownik mógł w międzyczasie zamknąć modal albo zmienić linię.
    if(pendingMytoSection===section){
      populateMytoBorderSelects(cache,section,savedForLine||{});
    }
  }catch(error){
    console.error('[D24 MYTO CZ] Lista przejść:',error);
    setMytoBorderStatus(
      `Nie udało się pobrać listy przejść: ${error.message}. Kliknij „Odśwież listę przejść”.`,
      'error'
    );
  }

  setTimeout(()=>els.mytoBaseAxles?.focus(),0);
}

export function closeMytoDialog(){
  pendingMytoSection=null;
  pendingMytoSavedSettings=null;
  els.mytoDialog?.classList.add('hidden');
  els.mytoDialog?.setAttribute('aria-hidden','true');
  document.body.style.overflow='';
}

export async function submitMytoDialog(event){
  event.preventDefault();
  if(!pendingMytoSection)return;

  const section=pendingMytoSection;
  const points=Array.isArray(section?.points)?section.points:[];
  const entryRequired=isMytoBorderPoint(points[0]);
  const exitRequired=isMytoBorderPoint(points.at(-1));

  configureMytoBorderRequirement(els.mytoEntryBorderField,els.mytoEntryBorder,entryRequired);
  configureMytoBorderRequirement(els.mytoExitBorderField,els.mytoExitBorder,exitRequired);
  if(!els.mytoForm.reportValidity())return;

  const entry=entryRequired?selectedMytoOption(els.mytoEntryBorder):{value:'',label:''};
  const exit=exitRequired?selectedMytoOption(els.mytoExitBorder):{value:'',label:''};

  if(entryRequired&&!entry.value){
    return setMytoBorderStatus('Wybierz dokładne przejście graniczne WJAZD z listy MYTO CZ.','warning');
  }
  if(exitRequired&&!exit.value){
    return setMytoBorderStatus('Wybierz dokładne przejście graniczne WYJAZD z listy MYTO CZ.','warning');
  }

  const settings={
    date:els.mytoDate.value,
    time:normalizeClock(els.mytoTime.value),
    vehicleCategory:els.mytoVehicleCategory.value,
    baseAxles:els.mytoBaseAxles.value,
    actualAxles:els.mytoActualAxles.value,
    emissionClass:els.mytoEmissionClass.value,
    co2Class:els.mytoCo2Class.value,
    entryBorderValue:entry.value,
    entryBorderLabel:entry.label,
    exitBorderValue:exit.value,
    exitBorderLabel:exit.label,
  };

  localStorage.setItem(MYTO_SETTINGS_KEY,JSON.stringify(settings));

  try{
    const existing=await loadLineCalculatorSettings(
      state.organizationId,
      state.currentRoute.lineNumber,
      'myto-cz'
    )||pendingMytoSavedSettings||{};

    const sectionKey=String(Number(section?.order||0));
    const previousSection=existing.sectionRoutes&&typeof existing.sectionRoutes==='object'
      ? (existing.sectionRoutes[sectionKey]||{})
      : {};

    const persistentSection={
      ...previousSection,
      entryBorderValue:settings.entryBorderValue,
      entryBorderLabel:settings.entryBorderLabel,
      exitBorderValue:settings.exitBorderValue,
      exitBorderLabel:settings.exitBorderLabel,
    };

    await saveLineCalculatorSettings(
      state.organizationId,
      state.currentRoute.lineNumber,
      'myto-cz',
      {
        ...existing,
        vehicleCategory:settings.vehicleCategory,
        baseAxles:settings.baseAxles,
        actualAxles:settings.actualAxles,
        emissionClass:settings.emissionClass,
        co2Class:settings.co2Class,

        // Top-level zachowujemy dla miesięcznego dispatchera i starszych ścieżek.
        entryBorderValue:settings.entryBorderValue,
        entryBorderLabel:settings.entryBorderLabel,
        exitBorderValue:settings.exitBorderValue,
        exitBorderLabel:settings.exitBorderLabel,

        // Jednocześnie zapisujemy per section_order, gdy linia kiedyś będzie miała
        // więcej niż jeden czeski odcinek.
        sectionRoutes:{
          ...(existing.sectionRoutes&&typeof existing.sectionRoutes==='object'?existing.sectionRoutes:{}),
          [sectionKey]:persistentSection,
        },
      }
    );
  }catch(error){
    console.error('[D24 MYTO CZ] Nie udało się zapisać parametrów linii:',error);
    return setStatus(els.extensionStatus,`Nie udało się zapisać parametrów MYTO CZ dla linii: ${error.message}`,'error');
  }

  closeMytoDialog();
  await runMytoCz(section,settings);
}

export function isMytoBorderPoint(point){
  return Boolean(
    point?.isBorder
    || point?.pointType==='border'
    || String(point?.countryCode||'').includes('/')
    || /^granica\b/i.test(String(point?.name||'').trim())
  );
}

function sameMytoRoutePoint(a,b){
  if(!a||!b)return false;
  if(a.id&&b.id&&String(a.id)===String(b.id))return true;
  const alat=Number(a.lat), alon=Number(a.lon), blat=Number(b.lat), blon=Number(b.lon);
  if([alat,alon,blat,blon].every(Number.isFinite)){
    if(Math.abs(alat-blat)<=0.00002&&Math.abs(alon-blon)<=0.00002)return true;
  }
  return false;
}

function uniqueMytoPoints(points){
  const out=[];
  const seen=new Set();
  for(const point of points||[]){
    if(!point)continue;
    const lat=Number(point.lat), lon=Number(point.lon);
    const key=point.id
      ?`id:${point.id}`
      :Number.isFinite(lat)&&Number.isFinite(lon)
        ?`gps:${lat.toFixed(5)},${lon.toFixed(5)}`
        :`name:${String(point.name||'').trim().toLowerCase()}`;
    if(!key||seen.has(key))continue;
    seen.add(key);
    out.push(point);
  }
  return out;
}

export function collectMytoCzRoutePoints(section){
  const sectionPoints=Array.isArray(section?.points)?section.points.filter(Boolean):[];
  if(sectionPoints.length<2)throw new Error('MYTO CZ: odcinek nie ma dwóch punktów krańcowych.');

  const startPoint=sectionPoints[0];
  const endPoint=sectionPoints.at(-1);
  const expanded=Array.isArray(state.currentRoute?.expandedPoints)?state.currentRoute.expandedPoints:[];

  const startIndex=expanded.findIndex(point=>sameMytoRoutePoint(point,startPoint));
  const endIndex=expanded.findIndex(point=>sameMytoRoutePoint(point,endPoint));
  let transitCandidates=[];

  // Najpewniejsze źródło punktów Przez: pełna kolejność trasy zapisana przez aplikację.
  // Dzięki temu nie gubimy np. Ostravy i Brna, nawet jeśli snapshot country_sections
  // ma tylko punkty graniczne.
  if(startIndex>=0&&endIndex>=0&&startIndex!==endIndex){
    const step=startIndex<endIndex?1:-1;
    for(let index=startIndex+step;index!==endIndex;index+=step){
      const point=expanded[index];
      if(!point||isMytoBorderPoint(point))continue;
      const country=String(point.countryCode||'').toUpperCase();
      // Punkt pomiędzy czeskimi granicami powinien należeć do CZ. Jeżeli starszy
      // snapshot nie ma countryCode, również go zachowujemy zamiast go zgubić.
      if(country&&country!=='CZ')continue;
      transitCandidates.push(point);
    }
  }

  // Fallback / uzupełnienie ze snapshotu samej sekcji.
  transitCandidates.push(...sectionPoints.slice(1,-1).filter(point=>!isMytoBorderPoint(point)));
  transitCandidates=uniqueMytoPoints(transitCandidates);

  const jobPoints=[startPoint,...transitCandidates,endPoint];
  if(jobPoints.length>5){
    const names=transitCandidates.map(point=>point.name||point.address||'punkt').join(' → ');
    throw new Error(
      `MYTO CZ: trasa ma ${jobPoints.length} punktów łącznie (${transitCandidates.length} punktów Przez), `+
      `a kalkulator dopuszcza maksymalnie 5: wjazd + 3 Przez + wyjazd. Punkty Przez: ${names}.`
    );
  }
  if(jobPoints.length<2)throw new Error('MYTO CZ: po przygotowaniu trasy brakuje punktów krańcowych.');
  return jobPoints;
}

export async function runMytoCz(section,settings){
  if(!await requireAuthenticatedSession('uruchomić kalkulator MYTO CZ'))return;

  const preparedSection=await prepareSectionMetrics(section,'czeskiego');
  if(!preparedSection)return;

  let jobPoints=[];
  try{
    jobPoints=collectMytoCzRoutePoints(preparedSection);
  }catch(error){
    window.alert(error.message);
    return setStatus(els.extensionStatus,error.message,'error');
  }

  // V21: MYTO CZ nie zgaduje już przejścia z GPS / borderRoadRef.
  // Dla punktu granicznego musi istnieć dokładny zapis option.value z listy MYTO.
  if(isMytoBorderPoint(jobPoints[0])&&!String(settings?.entryBorderValue||'').trim()){
    return setStatus(els.extensionStatus,'MYTO CZ: brak zapisanego przejścia granicznego WJAZD. Otwórz START i wybierz je z listy MYTO CZ.','error');
  }
  if(isMytoBorderPoint(jobPoints.at(-1))&&!String(settings?.exitBorderValue||'').trim()){
    return setStatus(els.extensionStatus,'MYTO CZ: brak zapisanego przejścia granicznego WYJAZD. Otwórz START i wybierz je z listy MYTO CZ.','error');
  }

  const pointCount=jobPoints.length;
  const transitCount=pointCount-2;
  if(!Number.isInteger(transitCount)||transitCount<0||transitCount>3){
    const message=`MYTO CZ obsługuje maksymalnie 5 punktów łącznie: wjazd + maks. 3 Przez + wyjazd. Przygotowano ${pointCount} punktów.`;
    window.alert(message);
    return setStatus(els.extensionStatus,message,'error');
  }

  const distanceKm=sectionDistanceKm(preparedSection);
  const durationText=Number.isFinite(Number(preparedSection.durationSeconds))?formatDuration(Number(preparedSection.durationSeconds)):'';
  const transitLabels=jobPoints.slice(1,-1).map(point=>String(point.name||point.address||'punkt').trim()).filter(Boolean);
  const jobId=crypto.randomUUID();
  const payload={
    jobId,
    calculatorKey:'myto-cz',
    transitCount,
    transitLabels,
    routeId:state.currentRoute.id,
    routeVersionId:state.currentRoute.versionId,
    routeTitle:state.currentRoute.title,
    sourceFile:state.currentRoute.sourceFileName,
    countryCode:'CZ',
    countryName:'Czechy',
    sectionId:preparedSection.id,
    sectionOrder:preparedSection.order,
    distanceKm,
    durationText,
    points:jobPoints.map(p=>({
      name:p.name,
      address:p.address||'',
      countryCode:p.countryCode||'',
      countryName:p.countryName||'',
      lat:p.lat,
      lon:p.lon,
      isBorder:Boolean(p.isBorder),
      pointType:p.pointType||'',
      borderName:p.borderName||'',
      requestedBorderPair:p.requestedBorderPair||p.countryCode||'',
      borderRoadRef:p.borderRoadRef||'',
      borderRoadRefs:Array.isArray(p.borderRoadRefs)?p.borderRoadRefs:[],
      borderRoadName:p.borderRoadName||'',
      borderRoadDistanceKm:Number.isFinite(Number(p.borderRoadDistanceKm))?Number(p.borderRoadDistanceKm):null,
      borderRoadSource:p.borderRoadSource||'',
    })),
    settings,
  };

  const viaText=transitLabels.length?` Przez: ${transitLabels.join(' → ')}.`:' Bez punktów Przez.';
  const borderText=[
    settings?.entryBorderLabel?`Wjazd: ${settings.entryBorderLabel}`:'',
    settings?.exitBorderLabel?`Wyjazd: ${settings.exitBorderLabel}`:'',
  ].filter(Boolean).join(' · ');
  setStatus(
    els.extensionStatus,
    `Uruchamiam MYTO CZ: ${pointCount} punktów łącznie, ${transitCount} Przez.${viaText}${borderText?` ${borderText}.`:''}`,
    'working'
  );

  try{
    await startBrowserlessCalculatorJob(payload);
  }catch(error){
    console.error('[D24 BROWSERLESS MYTO CZ]',error);
  }
}



export function loadMytoSettings(){
  try{return JSON.parse(localStorage.getItem(MYTO_SETTINGS_KEY)||'{}');}
  catch{return {};}
}


export function refreshCurrentMytoBorders() {
  return loadMytoBorderOptions({ force:true, showStatus:true })
    .then(options => populateMytoBorderSelects(options, pendingMytoSection, pendingMytoSavedSettings || {}))
    .catch(error => setMytoBorderStatus(`Nie udało się odświeżyć listy przejść: ${error.message}`, 'error'));
}
